// /client/src/components/ProgramForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const ProgramForm = ({ 
  initialData = {}, 
  onSubmit, 
  submitButtonText = 'Create Program',
  isLoading = false,
  mode = 'create' // 'create' or 'edit'
}) => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    programName: '',
    productId: '',
    timeframe: '',
    offering: 'Standard',
    offeringType: '',
    timeZone: 'America/Los_Angeles',
    podIds: [],
    ...initialData
  });
  
  const [errors, setErrors] = useState({});
  const [podIdInput, setPodIdInput] = useState('');
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setProductsLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          console.error('No authentication token found');
          return;
        }
        
        const response = await fetch('/api/products?status=active', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setProducts(data.data || []);
        } else {
          console.error('Failed to fetch products');
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Update form data when initialData changes (for edit mode)
  useEffect(() => {
    if (Object.keys(initialData).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData]);

  // Time zone options
  const timeZoneOptions = [
    'America/Los_Angeles',
    'America/Denver',
    'America/Chicago',
    'America/New_York',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu'
  ];

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || '' : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddPodId = (e) => {
    e.preventDefault();
    if (podIdInput.trim() && !formData.podIds.includes(podIdInput.trim())) {
      setFormData(prev => ({
        ...prev,
        podIds: [...prev.podIds, podIdInput.trim()]
      }));
      setPodIdInput('');
    }
  };

  const handleRemovePodId = (podIdToRemove) => {
    setFormData(prev => ({
      ...prev,
      podIds: prev.podIds.filter(id => id !== podIdToRemove)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.programName.trim()) {
      newErrors.programName = 'Program name is required';
    }
    
    if (formData.offering === 'Nuanced' && !formData.offeringType.trim()) {
      newErrors.offeringType = 'Offering type is required when Nuanced is selected';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    await onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      
      {/* Program Name - Required */}
      <div className="field">
        <label className="label">Program Name *</label>
        <div className="control">
          <input 
            className={`input ${errors.programName ? 'is-danger' : ''}`}
            type="text"
            name="programName"
            value={formData.programName}
            onChange={handleInputChange}
            placeholder="Enter program name"
          />
        </div>
        {errors.programName && (
          <p className="help is-danger">{errors.programName}</p>
        )}
      </div>

      {/* Product Selection - Optional */}
      <div className="field">
        <label className="label">Product</label>
        <div className="control">
          <div className={`select is-fullwidth ${errors.productId ? 'is-danger' : ''}`}>
            <select 
              name="productId"
              value={formData.productId}
              onChange={handleInputChange}
              disabled={productsLoading}
              className='select'
            >
              <option value="">
                {productsLoading ? 'Loading products...' : 'Select a product (optional)'}
              </option>
              {products.map((product) => (
                <option key={product._id} value={product._id}>
                  {product.name} ({product.days} messages)
                </option>
              ))}
            </select>
          </div>
        </div>
        {errors.productId && (
          <p className="help is-danger">{errors.productId}</p>
        )}
        {productsLoading && (
          <p className="help">Loading available products...</p>
        )}
      </div>

      {/* Offering */}
      <div className="field">
        <label className="label">Offering</label>
        <div className="control">
          <div className="select is-fullwidth">
            <select 
              name="offering"
              value={formData.offering}
              onChange={handleInputChange}
              className='select'
            >
              <option value="Standard">Standard</option>
              <option value="Nuanced">Nuanced</option>
            </select>
          </div>
        </div>
      </div>

      {/* Offering Type - Only show if Nuanced */}
      {formData.offering === 'Nuanced' && (
        <div className="field">
          <label className="label">Offering Type</label>
          <div className="control">
            <input 
              className={`input ${errors.offeringType ? 'is-danger' : ''}`}
              type="text"
              name="offeringType"
              value={formData.offeringType}
              onChange={handleInputChange}
              placeholder="Specify offering type"
            />
          </div>
          {errors.offeringType && (
            <p className="help is-danger">{errors.offeringType}</p>
          )}
        </div>
      )}

      {/* Time Zone */}
      <div className="field">
        <label className="label">Time Zone</label>
        <div className="control">
          <div className="select is-fullwidth">
            <select 
              name="timeZone"
              value={formData.timeZone}
              onChange={handleInputChange}
              className='select'
            >
              {timeZoneOptions.map(tz => (
                <option key={tz} value={tz}>
                  {tz.replace('_', ' ').replace('America/', '').replace('Pacific/', '')}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="field is-grouped pt-4 mt-5">
        <div className="control">
          <button 
            className={`button is-primary ${isLoading ? 'is-loading' : ''}`}
            type="submit"
            disabled={isLoading}
          >
            {submitButtonText}
          </button>
        </div>
        <div className="control">
          <button 
            className="button is-light"
            type="button"
            onClick={() => navigate('/programs')}
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
};

export default ProgramForm;